import React, { useEffect, useRef } from 'react';
import { View, Animated, StyleSheet, Dimensions } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

// ─── Moving sweep shimmer (Instagram-style) ───────────────────────────────────
const SHIMMER_COLORS = [
    'rgba(203,213,225,0.2)',
    'rgba(241,245,249,0.95)',
    'rgba(203,213,225,0.2)',
];

const SHINE_COLORS = [
    'rgba(6,182,212,0.0)',
    'rgba(103,232,249,0.18)',
    'rgba(6,182,212,0.0)',
];

/**
 * ShimmerBone — a single bone with a sweeping light animation.
 * `delay` staggers the start so multiple bones wave sequentially.
 */
export const ShimmerBone = ({ width = '100%', height = 14, borderRadius = 8, delay = 0, style }) => {
    const sweep = useRef(new Animated.Value(-1)).current;

    useEffect(() => {
        const loop = Animated.loop(
            Animated.sequence([
                Animated.delay(delay),
                Animated.timing(sweep, {
                    toValue: 1,
                    duration: 1100,
                    useNativeDriver: true,
                }),
                Animated.timing(sweep, {
                    toValue: -1,
                    duration: 0,
                    useNativeDriver: true,
                }),
                Animated.delay(400 - delay),
            ])
        );
        loop.start();
        return () => loop.stop();
    }, [sweep, delay]);

    const translateX = sweep.interpolate({
        inputRange: [-1, 1],
        outputRange: [-SCREEN_WIDTH * 1.2, SCREEN_WIDTH * 1.2],
    });

    return (
        <View
            style={[
                {
                    width,
                    height,
                    borderRadius,
                    backgroundColor: '#E2E8F0',
                    overflow: 'hidden',
                },
                style,
            ]}
        >
            <Animated.View
                style={{
                    ...StyleSheet.absoluteFillObject,
                    transform: [{ translateX }],
                }}
            >
                <LinearGradient
                    colors={SHIMMER_COLORS}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={{ width: SCREEN_WIDTH * 2.4, height: '100%' }}
                />
            </Animated.View>
        </View>
    );
};

// ─── Cyan-tinted bone for feature areas ───────────────────────────────────────
export const CyanShimmerBone = ({ width = '100%', height = 14, borderRadius = 8, delay = 0, style }) => {
    const sweep = useRef(new Animated.Value(-1)).current;

    useEffect(() => {
        const loop = Animated.loop(
            Animated.sequence([
                Animated.delay(delay),
                Animated.timing(sweep, {
                    toValue: 1,
                    duration: 1100,
                    useNativeDriver: true,
                }),
                Animated.timing(sweep, { toValue: -1, duration: 0, useNativeDriver: true }),
                Animated.delay(400 - delay),
            ])
        );
        loop.start();
        return () => loop.stop();
    }, [sweep, delay]);

    const translateX = sweep.interpolate({
        inputRange: [-1, 1],
        outputRange: [-SCREEN_WIDTH * 1.2, SCREEN_WIDTH * 1.2],
    });

    return (
        <View
            style={[
                {
                    width,
                    height,
                    borderRadius,
                    backgroundColor: 'rgba(6,182,212,0.08)',
                    overflow: 'hidden',
                },
                style,
            ]}
        >
            <Animated.View
                style={{
                    ...StyleSheet.absoluteFillObject,
                    transform: [{ translateX }],
                }}
            >
                <LinearGradient
                    colors={SHINE_COLORS}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={{ width: SCREEN_WIDTH * 2.4, height: '100%' }}
                />
            </Animated.View>
        </View>
    );
};

// ─── Post card skeleton ───────────────────────────────────────────────────────
export const PostCardSkeleton = ({ index = 0 }) => {
    const baseDelay = index * 80;
    const mountAnim = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        Animated.timing(mountAnim, {
            toValue: 1,
            duration: 350,
            delay: index * 120,
            useNativeDriver: true,
        }).start();
    }, []);

    return (
        <Animated.View
            style={[
                skeletonStyles.card,
                {
                    opacity: mountAnim,
                    transform: [{
                        translateY: mountAnim.interpolate({
                            inputRange: [0, 1],
                            outputRange: [18, 0],
                        }),
                    }],
                },
            ]}
        >
            {/* Header */}
            <View style={skeletonStyles.row}>
                <ShimmerBone width={44} height={44} borderRadius={22} delay={baseDelay} />
                <View style={skeletonStyles.flex}>
                    <ShimmerBone width="52%" height={13} borderRadius={7} delay={baseDelay + 60} style={{ marginBottom: 7 }} />
                    <ShimmerBone width="28%" height={10} borderRadius={5} delay={baseDelay + 120} />
                </View>
                <ShimmerBone width={24} height={24} borderRadius={12} delay={baseDelay + 80} />
            </View>

            {/* Text lines */}
            <ShimmerBone width="94%" height={13} borderRadius={6} delay={baseDelay + 140} style={{ marginTop: 16, marginBottom: 8 }} />
            <ShimmerBone width="78%" height={13} borderRadius={6} delay={baseDelay + 180} style={{ marginBottom: 8 }} />
            <ShimmerBone width="55%" height={13} borderRadius={6} delay={baseDelay + 220} />

            {/* Media placeholder */}
            <ShimmerBone width="100%" height={170} borderRadius={12} delay={baseDelay + 260} style={{ marginTop: 14 }} />

            {/* Action bar */}
            <View style={[skeletonStyles.row, { marginTop: 16, gap: 10 }]}>
                <ShimmerBone width={72} height={28} borderRadius={14} delay={baseDelay + 300} />
                <ShimmerBone width={72} height={28} borderRadius={14} delay={baseDelay + 340} />
                <ShimmerBone width={72} height={28} borderRadius={14} delay={baseDelay + 380} />
            </View>
        </Animated.View>
    );
};

// ─── Chat bubble skeleton ─────────────────────────────────────────────────────
const ChatBubbleSkeleton = ({ isRight = false, delay = 0, mountDelay = 0, bubbleWidth = 140 }) => {
    const mountAnim = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        Animated.spring(mountAnim, {
            toValue: 1,
            delay: mountDelay,
            tension: 80,
            friction: 9,
            useNativeDriver: true,
        }).start();
    }, []);

    return (
        <Animated.View
            style={[
                chatSkeletonStyles.wrapper,
                isRight ? chatSkeletonStyles.right : chatSkeletonStyles.left,
                {
                    opacity: mountAnim,
                    transform: [{
                        translateX: mountAnim.interpolate({
                            inputRange: [0, 1],
                            outputRange: [isRight ? 30 : -30, 0],
                        }),
                    }],
                },
            ]}
        >
            {!isRight && (
                <ShimmerBone width={32} height={32} borderRadius={16} delay={delay} style={{ marginRight: 8, flexShrink: 0 }} />
            )}
            <View style={chatSkeletonStyles.bubbleGroup}>
                <ShimmerBone width={bubbleWidth} height={42} borderRadius={16} delay={delay + 50} />
                <ShimmerBone
                    width={38}
                    height={9}
                    borderRadius={4}
                    delay={delay + 100}
                    style={{ marginTop: 5, alignSelf: isRight ? 'flex-end' : 'flex-start' }}
                />
            </View>
        </Animated.View>
    );
};

// ─── Typing indicator (3 bouncing dots) ──────────────────────────────────────
export const TypingDots = () => {
    const dots = [
        useRef(new Animated.Value(0)).current,
        useRef(new Animated.Value(0)).current,
        useRef(new Animated.Value(0)).current,
    ];

    useEffect(() => {
        const animations = dots.map((dot, i) =>
            Animated.loop(
                Animated.sequence([
                    Animated.delay(i * 160),
                    Animated.timing(dot, { toValue: -8, duration: 280, useNativeDriver: true }),
                    Animated.timing(dot, { toValue: 0, duration: 280, useNativeDriver: true }),
                    Animated.delay(600 - i * 160),
                ])
            )
        );
        Animated.parallel(animations).start();
        return () => animations.forEach(a => a.stop());
    }, []);

    return (
        <View style={typingStyles.container}>
            <View style={typingStyles.bubble}>
                {dots.map((dot, i) => (
                    <Animated.View
                        key={i}
                        style={[typingStyles.dot, { transform: [{ translateY: dot }] }]}
                    />
                ))}
            </View>
        </View>
    );
};

// ─── Full skeletons ───────────────────────────────────────────────────────────
export const FeedSkeleton = ({ count = 4 }) => (
    <View style={{ padding: 10 }}>
        {Array.from({ length: count }).map((_, i) => (
            <PostCardSkeleton key={i} index={i} />
        ))}
    </View>
);

const CHAT_PATTERN = [
    { isRight: false, width: 130 },
    { isRight: true, width: 160 },
    { isRight: false, width: 110 },
    { isRight: true, width: 90 },
    { isRight: false, width: 170 },
    { isRight: true, width: 140 },
    { isRight: false, width: 95 },
    { isRight: true, width: 120 },
    { isRight: false, width: 150 },
    { isRight: true, width: 80 },
];

export const ChatMessagesSkeleton = () => (
    <View style={chatSkeletonStyles.container}>
        {CHAT_PATTERN.map((item, i) => (
            <ChatBubbleSkeleton
                key={i}
                isRight={item.isRight}
                delay={i * 60}
                mountDelay={i * 80}
                bubbleWidth={item.width}
            />
        ))}
    </View>
);

export const ChatRowSkeleton = ({ index = 0 }) => {
    const mountAnim = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        Animated.timing(mountAnim, {
            toValue: 1,
            duration: 300,
            delay: index * 70,
            useNativeDriver: true,
        }).start();
    }, []);

    return (
        <Animated.View
            style={[
                skeletonStyles.chatRow,
                {
                    opacity: mountAnim,
                    transform: [{ translateX: mountAnim.interpolate({ inputRange: [0, 1], outputRange: [-16, 0] }) }],
                },
            ]}
        >
            <ShimmerBone width={54} height={54} borderRadius={27} delay={index * 40} />
            <View style={skeletonStyles.flex}>
                <View style={[skeletonStyles.row, { marginBottom: 7 }]}>
                    <ShimmerBone width="44%" height={13} borderRadius={6} delay={index * 40 + 60} />
                    <ShimmerBone width="16%" height={10} borderRadius={5} delay={index * 40 + 80} />
                </View>
                <ShimmerBone width="68%" height={11} borderRadius={5} delay={index * 40 + 100} />
            </View>
        </Animated.View>
    );
};

export const ChatListSkeleton = ({ count = 8 }) => (
    <View style={{ paddingVertical: 4 }}>
        {Array.from({ length: count }).map((_, i) => (
            <ChatRowSkeleton key={i} index={i} />
        ))}
    </View>
);

/**
 * SmoothFeedLoader — a centered, high-end pulse animation for empty/loading states.
 */
export const SmoothFeedLoader = ({ text = "Checking for new posts..." }) => {
    const pulse = useRef(new Animated.Value(1)).current;
    const rotate = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        const pulseAnim = Animated.loop(
            Animated.sequence([
                Animated.timing(pulse, { toValue: 1.15, duration: 1200, useNativeDriver: true }),
                Animated.timing(pulse, { toValue: 1, duration: 1200, useNativeDriver: true }),
            ])
        );
        
        const rotateAnim = Animated.loop(
            Animated.timing(rotate, { toValue: 1, duration: 2500, useNativeDriver: true })
        );

        pulseAnim.start();
        rotateAnim.start();

        return () => {
            pulseAnim.stop();
            rotateAnim.stop();
        };
    }, []);

    const rotation = rotate.interpolate({
        inputRange: [0, 1],
        outputRange: ['0deg', '360deg'],
    });

    return (
        <View style={smoothStyles.container}>
            <View style={smoothStyles.ringWrapper}>
                <Animated.View style={[smoothStyles.ring, { transform: [{ rotate: rotation }, { scale: pulse }] }]}>
                    <LinearGradient
                        colors={['#06b6d4', 'transparent', '#0891b2']}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                        style={smoothStyles.gradientRing}
                    />
                </Animated.View>
                <View style={smoothStyles.innerCircle}>
                     <LinearGradient
                        colors={['#0891b2', '#0e7490']}
                        style={smoothStyles.innerGradient}
                    />
                </View>
            </View>
            <Animated.Text style={[smoothStyles.text, { opacity: pulse.interpolate({ inputRange: [1, 1.15], outputRange: [0.5, 0.9] }) }]}>
                {text}
            </Animated.Text>
        </View>
    );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const smoothStyles = StyleSheet.create({
    container: {
        paddingVertical: 100,
        alignItems: 'center',
        justifyContent: 'center',
    },
    ringWrapper: {
        width: 54,
        height: 54,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 16,
    },
    ring: {
        position: 'absolute',
        width: 50,
        height: 50,
        borderRadius: 25,
        borderWidth: 1.5,
        borderColor: 'rgba(6,182,212,0.1)',
        padding: 2,
    },
    gradientRing: {
        width: '100%',
        height: '100%',
        borderRadius: 25,
        opacity: 0.8,
    },
    innerCircle: {
        width: 10,
        height: 10,
        borderRadius: 5,
        backgroundColor: '#0891b2',
        shadowColor: '#06b6d4',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.6,
        shadowRadius: 8,
        elevation: 6,
    },
    innerGradient: {
        width: '100%',
        height: '100%',
        borderRadius: 5,
    },
    text: {
        fontSize: 13,
        fontWeight: '600',
        color: '#64748b',
        letterSpacing: 0.3,
    }
});

const skeletonStyles = StyleSheet.create({
    card: {
        backgroundColor: '#FFFFFF',
        borderRadius: 16,
        padding: 14,
        marginBottom: 10,
        shadowColor: '#0891b2',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.06,
        shadowRadius: 8,
        elevation: 3,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    flex: {
        flex: 1,
        marginLeft: 12,
    },
    chatRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 13,
        gap: 14,
    },
});

const chatSkeletonStyles = StyleSheet.create({
    container: {
        flex: 1,
        padding: 14,
    },
    wrapper: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        marginBottom: 14,
    },
    left: { justifyContent: 'flex-start' },
    right: { justifyContent: 'flex-end' },
    bubbleGroup: { maxWidth: '72%' },
});

const typingStyles = StyleSheet.create({
    container: {
        alignSelf: 'flex-start',
        marginLeft: 14,
        marginBottom: 8,
    },
    bubble: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        borderRadius: 20,
        paddingHorizontal: 14,
        paddingVertical: 10,
        gap: 5,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.06,
        shadowRadius: 3,
        elevation: 2,
    },
    dot: {
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: '#0891b2',
    },
});
